import React from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Link, Button,
} from '@mui/material';
import {
  Extension as ExtensionIcon,
  CloudOff as OfflineIcon,
  WarningAmber as WarnIcon,
  Lock as LockIcon,
  OpenInNew as ExternalIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

/**
 * Extension v5 — Popup error states
 *
 * End-user surface. Three degraded popup states:
 *  1. Offline — can't reach Automatrix API
 *  2. Out of date — extension version too old, features disabled
 *  3. Unauthenticated — no auth token; sign in via AchieveDXP
 *
 * All fit the 300x200 Chrome popup dimensions.
 */

const TEAL = '#1ebea5';
const DEEP_TEAL = '#0b847f';
const RED = '#c62828';
const ORANGE = '#e65100';
const POPUP_W = 300;

const MiniAchieveDxpIcon = ({ size = 20, error = false }) => (
  <svg width={size} height={size} viewBox="0 0 16 16">
    <defs>
      <linearGradient id="err-popup-bg" x1="0" x2="1" y1="0" y2="1">
        <stop offset="0%" stopColor={TEAL} />
        <stop offset="100%" stopColor={DEEP_TEAL} />
      </linearGradient>
    </defs>
    <rect x="1" y="1" width="14" height="14" rx="3" fill="url(#err-popup-bg)" />
    <path d="M 5 12 L 8 4 L 11 12 M 6.2 9.5 L 9.8 9.5"
          stroke="#fff" strokeWidth="1.2" fill="none"
          strokeLinecap="round" strokeLinejoin="round" />
    {error && (
      <circle cx="13" cy="13" r="2.3" fill="#e53935" stroke="#fff" strokeWidth="0.6" />
    )}
  </svg>
);

const PopupFrame = ({ children, tint = TEAL, deep = DEEP_TEAL, h = 200 }) => (
  <Paper elevation={6} sx={{
    width: POPUP_W, height: h,
    bgcolor: '#fff', borderRadius: 1.5, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    border: '1px solid #d5d9df',
    fontFamily: '-apple-system, system-ui, sans-serif',
  }}>
    <Box sx={{
      px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1,
      background: `linear-gradient(135deg, ${tint} 0%, ${deep} 100%)`,
      color: '#fff',
    }}>
      <MiniAchieveDxpIcon size={20} error />
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: 0.2 }}>
        AchieveDXP
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      <Typography sx={{
        fontSize: '0.68rem', fontFamily: 'monospace',
        bgcolor: 'rgba(255,255,255,0.18)', px: 0.75, py: 0.1,
        borderRadius: 0.5, fontWeight: 600,
      }}>
        v5.0.0
      </Typography>
    </Box>
    {children}
  </Paper>
);

const Card = ({ label, state, children, copy }) => (
  <Stack alignItems="center" spacing={1}>
    <Typography variant="caption" sx={{
      fontWeight: 700, fontSize: '0.72rem', color: '#546e7a',
      textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {label}
    </Typography>
    {state}
    <Typography variant="caption" sx={{
      fontSize: '0.72rem', color: '#90a4ae',
      maxWidth: 280, textAlign: 'center',
    }}>
      {copy}
    </Typography>
  </Stack>
);

const ExtV5ErrorStateMockup = () => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 3, p: 4, bgcolor: '#eef1f5', minHeight: 'calc(100vh - 180px)',
  }}>
    <Box sx={{ textAlign: 'center', maxWidth: 700 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
        <ExtensionIcon sx={{ fontSize: 24, color: TEAL }} />
        <Typography variant="h6" fontWeight={700}>
          Popup · degraded states
        </Typography>
        <Chip label="END USER" size="small" sx={{ fontWeight: 700, bgcolor: '#e8f5e9', color: '#1b5e20' }} />
        <Chip label="300 × 200" size="small" sx={{ fontWeight: 600, bgcolor: '#ede7f6', color: '#0b847f' }} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        The same 300×200 popup in failure modes. Each explains the fault
        plainly and points to a single corrective action. No stack traces,
        no script-level detail — those live on the dashboard.
      </Typography>
    </Box>

    <Box sx={{
      display: 'flex', gap: 6, alignItems: 'flex-start',
      flexWrap: 'wrap', justifyContent: 'center',
    }}>
      {/* Offline */}
      <Card
        label="1. Offline"
        copy="Cache might still work for a few minutes, so we don't panic — just disclose."
        state={
          <PopupFrame tint="#b71c1c" deep="#7f0000">
            <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <OfflineIcon sx={{ fontSize: 14, color: RED }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: RED }}>
                  Can't reach AchieveDXP
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 1 }}>
                Scripts from cache may be stale (last update 8m&nbsp;ago).
              </Typography>
              <Box sx={{
                bgcolor: '#ffebee', border: '1px solid #ef9a9a',
                borderRadius: 1, px: 1, py: 0.75,
              }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#c62828' }}>
                  Check your internet or try again in a minute.
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              px: 1.5, py: 0.75, borderTop: '1px solid #eceff2', bgcolor: '#fff5f5',
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <Button
                size="small" startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none', fontSize: '0.7rem', py: 0.1,
                  color: RED, fontWeight: 600,
                }}
              >
                Retry now
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Typography sx={{ fontSize: '0.65rem', color: '#90a4ae', fontFamily: 'monospace' }}>
                last ok 14:02
              </Typography>
            </Box>
          </PopupFrame>
        }
      />

      {/* Out of date */}
      <Card
        label="2. Out of date"
        copy="Features disabled when extension is too old to trust. User goes to dashboard to re-download."
        state={
          <PopupFrame tint="#ef6c00" deep="#bf360c">
            <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <WarnIcon sx={{ fontSize: 14, color: ORANGE }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: ORANGE }}>
                  Update required
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 1 }}>
                Your extension is v4.8.1. Minimum supported is v5.0.0.
              </Typography>
              <Box sx={{
                bgcolor: '#fff3e0', border: '1px solid #ffcc80',
                borderRadius: 1, px: 1, py: 0.75,
              }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#e65100' }}>
                  Features disabled until updated.
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              px: 1.5, py: 0.75, borderTop: '1px solid #eceff2', bgcolor: '#fff8e1',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
            }}>
              <Link
                href="#" underline="none"
                sx={{
                  fontSize: '0.72rem', color: ORANGE, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 0.25,
                }}
              >
                Download latest
                <ExternalIcon sx={{ fontSize: 10 }} />
              </Link>
            </Box>
          </PopupFrame>
        }
      />

      {/* Unauthenticated */}
      <Card
        label="3. Unauthenticated"
        copy="No token means no scripts, no filters. Sign-in redirects to AchieveDXP which forwards token via frontend-bridge."
        state={
          <PopupFrame tint="#455a64" deep="#263238">
            <Box sx={{ px: 1.5, py: 1.25, flexGrow: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <LockIcon sx={{ fontSize: 14, color: '#455a64' }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#263238' }}>
                  Sign in to continue
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.72rem', color: '#546e7a', ml: 2.25, mb: 1 }}>
                Sign in to AchieveDXP before we can help.
              </Typography>
              <Box sx={{
                bgcolor: '#eceff1', border: '1px solid #cfd8dc',
                borderRadius: 1, px: 1, py: 0.75,
              }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#37474f' }}>
                  No scripts or filters are loaded.
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              px: 1.5, py: 0.75, borderTop: '1px solid #eceff2', bgcolor: '#f7f9fb',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
            }}>
              <Link
                href="#" underline="none"
                sx={{
                  fontSize: '0.72rem', color: TEAL, fontWeight: 600,
                  display: 'inline-flex', alignItems: 'center', gap: 0.25,
                }}
              >
                Sign in via AchieveDXP
                <ExternalIcon sx={{ fontSize: 10 }} />
              </Link>
            </Box>
          </PopupFrame>
        }
      />
    </Box>

    <Paper elevation={0} sx={{
      maxWidth: 700, p: 2, bgcolor: '#fff3cd',
      border: '1px solid #ffe082', borderRadius: 1.5,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#5d4037', fontSize: '0.75rem' }}>
        DESIGN RULE
      </Typography>
      <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#4e342e', mt: 0.5 }}>
        Each degraded state gives one corrective action and no jargon. The toolbar
        icon also shows its red-dot overlay in all three of these cases — so the
        user knows something needs attention before they even click.
      </Typography>
    </Paper>
  </Box>
);

export default ExtV5ErrorStateMockup;
